import { Injectable } from '@angular/core';
import { ArrayHelper } from 'apps/idl/src/helpers/arrayHelper';
import { IStoreDocument } from 'apps/idl/src/model';
import { IGalleryFile } from 'apps/idl/src/model/gallery/IGalleryFile';
import { EDatabaseRole } from 'apps/idl/src/model/store/EDatabaseRole';
import { IDataSource } from 'apps/idl/src/model/store/IDataSource';
import { IStoreDataResponse } from 'apps/idl/src/model/store/IStoreDataResponse';
import { DmsExternalLinkerService } from 'apps/idl/src/modules/dms/services/dms-external-linker.service';
import { GalleryService } from 'apps/idl/src/services/security/gallery.service';
import { Store } from 'apps/idl/src/services/store.service';
import { plainToClass } from 'class-transformer';
import { EMPTY, Observable, of } from 'rxjs';
import { map, mapTo, mergeMap, tap } from 'rxjs/operators';
import { C_PREFIX_ORDONNANCE } from '../../../app/app.constants';
import { Acte } from '../../../model/Acte';
import { Traitement } from '../../../model/Traitement';
import { IOrdonnance } from '../models/iordonnance';
import { Ordonnance } from '../models/ordonnance';

@Injectable()
export class OrdonnancesService {

	//#region METHODS

	constructor(
		private readonly isvcStore: Store,
		private readonly isvcGallery: GalleryService,
		private readonly isvcDmsExternalLinker: DmsExternalLinkerService,
	) { }

	/** Récupère toutes les ordonnances d'un traitement.
	 * @param psTraitementId
	 * @param pbLive
	 */
	public getTraitementOrdonnances(psTraitementId: string, pbLive?: boolean): Observable<Ordonnance[]> {
		const loDatasource: IDataSource = {
			role: EDatabaseRole.workspace,
			viewParams: {
				startkey: `${C_PREFIX_ORDONNANCE}${psTraitementId}`,
				endkey: `${C_PREFIX_ORDONNANCE}${psTraitementId}${Store.C_ANYTHING_CODE_ASCII}`,
				include_docs: true
			},
			live: pbLive
		};

		return this.isvcStore.get<Ordonnance>(loDatasource)
			.pipe(map((paOrdonnances: IOrdonnance[]) => paOrdonnances.map((poOrdonnance: IOrdonnance) =>
				poOrdonnance instanceof Ordonnance ? poOrdonnance : plainToClass(Ordonnance, poOrdonnance)
			)));
	}

	/** Récupère toutes les ordonnances des traitements.
	 * @param paTraitementsIds
	 * @param pbLive
	 */
	public getOrdonnancesByTraitementId(paTraitementsIds: string[], pbLive?: boolean): Observable<Map<string, Ordonnance[]>> {
		const loDatasource: IDataSource = {
			role: EDatabaseRole.workspace,
			viewParams: {
				startkey: `${C_PREFIX_ORDONNANCE}`,
				endkey: `${C_PREFIX_ORDONNANCE}${Store.C_ANYTHING_CODE_ASCII}`
			},
			live: pbLive
		};

		return this.isvcStore.get<IStoreDocument>(loDatasource)
			.pipe(
				map((paOrdonnanceDocs: IStoreDocument[]) => paOrdonnanceDocs
					.map((poDoc: IStoreDocument) => poDoc._id)
					.filter((psOrdonnanceId: string) => paTraitementsIds.some((psTraitementId: string) => psOrdonnanceId.includes(psTraitementId)))
				),
				mergeMap((paOrdonnancesIds: string[]) => this.getOrdonnancesByIds(paOrdonnancesIds)),
				map((paOrdonnances: Ordonnance[]) => {
					const loOrdonnancesByTraitementId = new Map<string, Ordonnance[]>();

					paOrdonnances.forEach((poOrdonnance: Ordonnance) => {
						if (loOrdonnancesByTraitementId.has(poOrdonnance.traitementId))
							loOrdonnancesByTraitementId.set(poOrdonnance.traitementId, ArrayHelper.unique([...loOrdonnancesByTraitementId.get(poOrdonnance.traitementId), poOrdonnance]));
						else
							loOrdonnancesByTraitementId.set(poOrdonnance.traitementId, [poOrdonnance]);
					});

					return loOrdonnancesByTraitementId;
				})
			);
	}

	/** Récupère l'ordonnance selon l'identifiant.
	 * @param psId
	 */
	public getOrdonnance(psId: string, pbLive?: boolean): Observable<Ordonnance> {
		const loDatasource: IDataSource = {
			role: EDatabaseRole.workspace,
			viewParams: {
				key: psId,
				include_docs: true
			},
			live: pbLive
		};

		return this.isvcStore.getOne<Ordonnance>(loDatasource).pipe(map((poOrdonnance: IOrdonnance) => plainToClass(Ordonnance, poOrdonnance)));
	}

	/** Récupère les ordonnances selon les identifiants.
	 * @param paIds
	 */
	public getOrdonnancesByIds(paIds: string[]): Observable<Ordonnance[]> {
		const loDatasource: IDataSource = {
			role: EDatabaseRole.workspace,
			viewParams: {
				keys: paIds,
				include_docs: true
			}
		};

		return this.isvcStore.get<Ordonnance>(loDatasource)
			.pipe(map((paOrdonnances: IOrdonnance[]) => paOrdonnances.map((poOrdonnance: Ordonnance) => plainToClass(Ordonnance, poOrdonnance))));
	}

	/** Sauvegarde plusieurs ordonnances.
	 * @param paOrdonnances
	 */
	public saveOrdonnances(paOrdonnances: Ordonnance[]) {
		const lsDatabaseId: string = ArrayHelper.getFirstElement(this.isvcStore.getDatabasesIdsByRole(EDatabaseRole.workspace));

		return this.isvcStore.putMultipleDocuments(paOrdonnances, lsDatabaseId)
			.pipe(
				mapTo(paOrdonnances)
			);
	}

	/** Sauvegarde une ordonnance d'un traitement.
	 * @param poTraitement
	 * @param poOrdonnance
	 */
	public saveOrdonnance(poTraitement: Traitement, poOrdonnance: Ordonnance): Observable<Ordonnance> {
		const lsDatabaseId: string = ArrayHelper.getFirstElement(this.isvcStore.getDatabasesIdsByRole(EDatabaseRole.workspace));

		return this.saveDocuments(poTraitement, poOrdonnance)
			.pipe(
				mergeMap(() => {
					poOrdonnance.documents?.forEach((poGalleryFile: IGalleryFile) => {
						delete poGalleryFile.isAvailable;
						delete poGalleryFile.isNew;
						delete poGalleryFile.isLoading;
						delete poGalleryFile.file;
					});
					return this.isvcStore.put(poOrdonnance, lsDatabaseId);
				}),
				mapTo(poOrdonnance)
			);
	}

	/** Sauvegarde les documents d'une ordonnance d'un traitement.
	 * @param poTraitement
	 * @param poOrdonnance Ordonnance dont il faut enregistrer les documents.
	 */
	private saveDocuments(poTraitement: Traitement, poOrdonnance: Ordonnance): Observable<boolean> {
		if (ArrayHelper.hasElements(poOrdonnance.documents)) {
			poOrdonnance.documents.forEach((poDoc: IGalleryFile) => poDoc.type = "");

			return this.isvcGallery.saveFiles(poOrdonnance.documents).pipe(
				mergeMap(() => this.isvcDmsExternalLinker.linkFilesToEntity(poTraitement, poOrdonnance.documents))
			);
		}

		return of(true);
	}

	/** Supprime une ordonnance.
	 * @param poOrdonnance
	 */
	public deleteOrdonnance(poOrdonnance: Ordonnance): Observable<boolean> {
		return this.isvcStore.delete(poOrdonnance).pipe(map((poStoreDataResponse: IStoreDataResponse) => poStoreDataResponse.ok));
	}

	/** Permet de lier des ordonnances à un acte à l'aide d'un selecteur.
	 * @param paOrdonnances
	 * @param poActe
	 * @param poOrdonnancesIdsByActeGuid
	 * @param paPreselectedOrdonnanceIds
	 */
	public updateOrdonnancesLinksToActe(
		paOrdonnances: Ordonnance[],
		paSelectedOrdonnances: Ordonnance[],
		poActe: Acte,
		poOrdonnancesIdsByActeGuid: Map<string, string[]>
	): Observable<Ordonnance[]> {
		const laPreviousSelectedOrdonnancesIds: string[] = poOrdonnancesIdsByActeGuid.get(poActe.guid) ?? [];
		const loOrdonnancesById = new Map<string, Ordonnance>(paOrdonnances.map((poOrdonnance: Ordonnance) => [poOrdonnance._id, poOrdonnance]));

		if (paSelectedOrdonnances === undefined)
			return EMPTY;
		else {
			const laSelectedOrdonnancesIds: string[] = paSelectedOrdonnances.map((poOrdonnance: Ordonnance) => poOrdonnance._id);
			const laOrdonnancesIdsToUnlink: string[] = ArrayHelper.getDifferences(laPreviousSelectedOrdonnancesIds, laSelectedOrdonnancesIds);
			const laOrdonnancesGuidsToLink: string[] = ArrayHelper.getDifferences(laSelectedOrdonnancesIds, laPreviousSelectedOrdonnancesIds);

			const laOrdonnancesToSave: Ordonnance[] = [];

			laOrdonnancesIdsToUnlink.forEach((psOrdonnanceId: string) => {
				const loOrdonnance: Ordonnance = loOrdonnancesById.get(psOrdonnanceId);
				ArrayHelper.removeElement(loOrdonnance.linkedActesGuids, poActe.guid);
				laOrdonnancesToSave.push(loOrdonnance);
			});

			laOrdonnancesGuidsToLink.forEach((psOrdonnanceId: string) => {
				const loOrdonnance: Ordonnance = loOrdonnancesById.get(psOrdonnanceId);
				loOrdonnance.linkedActesGuids.push(poActe.guid);
				laOrdonnancesToSave.push(loOrdonnance);
			});

			return this.saveOrdonnances(laOrdonnancesToSave);
		}
	}

	public createNewOrdonnance(poTraitement: Traitement, paAllOrdonnances: Ordonnance[] = []): Observable<Ordonnance> {
		const laLinkedActesGuids: string[] = ArrayHelper.flat(paAllOrdonnances.map((poOrdonnance: Ordonnance) => poOrdonnance.linkedActesGuids));
		return this.editOrdonnance(poTraitement, new Ordonnance(poTraitement))
			.pipe(
				mergeMap((poOrdonnance: Ordonnance) => {
					return of(null);
				})
			);
	}

	public editOrdonnance(poTraitement: Traitement, poOrdonnance: Ordonnance): Observable<Ordonnance> {
		return of(null);
	}

	/** Retourne les ids d'ordonnances groupés par guid d'acte lié.
	 * @param paOrdonnances
	 */
	public getOrdonnancesIdsByActeGuid(paOrdonnances: Ordonnance[]): Map<string, string[]> {
		const loOrdonnancesIdsByActeId = new Map<string, string[]>();

		paOrdonnances.forEach((poOrdonnance: Ordonnance) => {
			if (ArrayHelper.hasElements(poOrdonnance.linkedActesGuids)) {
				poOrdonnance.linkedActesGuids.forEach((psActeGuid: string) => {
					if (loOrdonnancesIdsByActeId.has(psActeGuid))
						loOrdonnancesIdsByActeId.set(psActeGuid, ArrayHelper.unique([...loOrdonnancesIdsByActeId.get(psActeGuid), poOrdonnance._id]));
					else
						loOrdonnancesIdsByActeId.set(psActeGuid, [poOrdonnance._id]);
				});
			}
		});

		return loOrdonnancesIdsByActeId;
	}

	/** Retourne les guids d'actes par ordonnance liée.
	 * @param paOrdonnances
	 */
	public getActesByOrdonnanceId(paOrdonnances: Ordonnance[]): Map<string, string[]> {
		const loActesByOrdonnance = new Map<string, string[]>();

		paOrdonnances.forEach((poOrdonnance: Ordonnance) => {
			if (ArrayHelper.hasElements(poOrdonnance.linkedActesGuids)) {
				poOrdonnance.linkedActesGuids.forEach((psActeGuid: string) => {
					if (loActesByOrdonnance.has(poOrdonnance._id))
						loActesByOrdonnance.set(poOrdonnance._id, ArrayHelper.unique([...loActesByOrdonnance.get(poOrdonnance._id), psActeGuid]));
					else
						loActesByOrdonnance.set(poOrdonnance._id, [psActeGuid]);
				});
			}
		});

		return loActesByOrdonnance;
	}

	public linkOrdonnancesToActe(paOrdonnances: Ordonnance[], psActeGuid: string): Observable<Ordonnance[]> {
		paOrdonnances.forEach((poOrdonnance: Ordonnance) => poOrdonnance.linkedActesGuids.push(psActeGuid));
		return this.saveOrdonnances(paOrdonnances);
	}

	public unlinkOrdonnancesToActe(paOrdonnances: Ordonnance[], psActeGuid: string): Observable<Ordonnance[]> {
		paOrdonnances.forEach((poOrdonnance: Ordonnance) => ArrayHelper.removeElement(poOrdonnance.linkedActesGuids, psActeGuid));
		return this.saveOrdonnances(paOrdonnances);
	}

	//#endregion

}